import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { generateCaseWhenSql, CaseWhenCondition } from '../utils/sqlGenerator';

interface CaseWhenBuilderProps {
  columnName: string;
  onSqlGenerate: (sql: string) => void;
}

export const CaseWhenBuilder: React.FC<CaseWhenBuilderProps> = ({
  columnName,
  onSqlGenerate,
}) => {
  const [conditions, setConditions] = useState<CaseWhenCondition[]>([
    { condition: `${columnName} = ''`, result: '' }
  ]);
  const [elseResult, setElseResult] = useState<string>('');

  useEffect(() => {
    if (conditions.length > 0) {
      const sql = generateCaseWhenSql(columnName, conditions, elseResult);
      onSqlGenerate(sql);
    }
  }, [conditions, elseResult, columnName, onSqlGenerate]);

  const handleAddCondition = () => {
    setConditions([...conditions, { condition: `${columnName} = ''`, result: '' }]);
  };

  const handleRemoveCondition = (index: number) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((_, i) => i !== index));
    }
  };

  const handleConditionChange = (index: number, field: 'condition' | 'result', value: string) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setConditions(newConditions);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="mb-2">Conditions (WHEN-THEN)</Label>
        <div className="space-y-3">
          {conditions.map((condition, index) => (
            <div key={index} className="border rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Condition {index + 1}</span>
                {conditions.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveCondition(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div>
                <Label className="text-xs">WHEN (condition)</Label>
                <Input
                  placeholder={`${columnName} = 'value'`}
                  value={condition.condition}
                  onChange={(e) => handleConditionChange(index, 'condition', e.target.value)}
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Examples: {columnName} = &apos;active&apos;, {columnName} &gt; 100, {columnName} IS NOT NULL
                </div>
              </div>
              <div>
                <Label className="text-xs">THEN (result)</Label>
                <Input
                  placeholder="Result value"
                  value={condition.result}
                  onChange={(e) => handleConditionChange(index, 'result', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddCondition}
          className="mt-2"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Condition
        </Button>
      </div>

      <div>
        <Label>ELSE (default value)</Label>
        <Input
          placeholder="Default value (optional)"
          value={elseResult}
          onChange={(e) => setElseResult(e.target.value)}
        />
        <div className="text-xs text-muted-foreground mt-1">
          Value to return if no conditions match (leave empty for NULL)
        </div>
      </div>

      <div className="text-sm text-muted-foreground">
        <strong>Example:</strong>
        <div className="mt-1 font-mono text-xs bg-muted p-2 rounded">
          CASE<br />
          &nbsp;&nbsp;WHEN status = &apos;active&apos; THEN &apos;Active&apos;<br />
          &nbsp;&nbsp;WHEN status = &apos;pending&apos; THEN &apos;Pending&apos;<br />
          &nbsp;&nbsp;ELSE &apos;Unknown&apos;<br />
          END
        </div>
      </div>
    </div>
  );
};
